import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { FaLock } from 'react-icons/fa6';
import dayjs from 'dayjs';
import { PromoterOfferTier } from '../../../TierSelection';
import { OutgoingEventProductDto } from 'api/dtos/events/products';

interface Props {
  id: string;
  product: PromoterOfferTier;
  onSelect: (id: string) => void;
  isSelected: boolean;
  isDisabled: boolean;
}

const TierRow = forwardRef<HTMLLIElement, Props>(
  ({ product, id, onSelect, isSelected, isDisabled }, ref) => {
    const handleSelect = () => {
      if (isSelected || !isDisabled) {
        onSelect(id);
      }
    };
    const tier = product as OutgoingEventProductDto;
    const requiresApproval =
      tier.purchaseRequirements?.requiresApproval ?? false;
    const numberOfQuestions = tier.purchaseRequirements?.questions?.length ?? 0;
    const hasMaxQuantity = tier?.maxQuantity && tier?.maxQuantity > 0;

    return (
      <li
        ref={ref}
        className={classNames(
          'rounded-2xl p-4 items-center gap-3 mr-4 cursor-pointer border border-[#DCDCDC] ',
          isSelected && 'bg-primary-50',
          !isSelected && isDisabled && 'opacity-50 cursor-not-allowed'
        )}
        onClick={handleSelect}
      >
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-col flex-1">
            <div className="flex flex-row">
              {tier.isPrivate && <FaLock className="mr-2 mt-1 self-start" />}
              <div className="font-normal text-base text-[#202020]">
                {tier.name}
              </div>
            </div>
            <div className="text-sm font-bold text-[#202020]">
              ${tier.price}
            </div>
          </div>
          <input
            className="mt-1 mr-2"
            type="checkbox"
            checked={isSelected}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onChange={() => {}}
          />
        </div>
        {tier.description && (
          <div className="flex flex-row items-center justify-between my-2">
            <div className="text-sm font-normal text-[#202020]">
              {tier.description}
            </div>
          </div>
        )}
        <div className="h-px bg-[#EBEBEB] my-4" />
        <div>
          <div className="font-normal text-sm flex gap-2 align-center pb-2">
            <img
              alt="Ticket icon"
              src="/img/icons/ticket-admit-one.svg"
              width={16}
              height={16}
            />
            {tier.purchases}{' '}
            {hasMaxQuantity ? `/${tier.maxQuantity} sold` : 'sold'}
          </div>
          {requiresApproval && (
            <div className="font-normal text-sm flex gap-2 align-center pb-2">
              <div className="self-start">👍</div>
              <div>
                Requires Approval{' '}
                {numberOfQuestions > 0 && `(${numberOfQuestions} q's)`}
              </div>
            </div>
          )}
          {(tier.startDate || tier.endDate) && (
            <div className="flex flex-row items-center gap-2">
              <div className="self-start">⏰</div>
              <div className="flex flex-col">
                {tier.startDate && (
                  <div className="font-normal text-sm flex gap-2 align-center pb-2">
                    Start Date:{' '}
                    {dayjs(tier.startDate).format('MMM D, YYYY h:mm A')}
                  </div>
                )}
                {tier.endDate && (
                  <div className="font-normal text-sm flex gap-2 align-center pb-2">
                    End Date: {dayjs(tier.endDate).format('MMM D, YYYY h:mm A')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </li>
    );
  }
);

export default TierRow;
